import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { userActions } from '@akademiasaas/shared';
import { useQuery } from '~/hooks/useQuery';
import { useAppDispatch } from '~/initializeStore';
import { Callout } from '~/components/ui';
import AuthCard, { AuthHeading } from '../../layout/AuthCard';
import EmailForm from '../../form/EmailForm';
import { authErrorKind } from '../../form/authErrors';
import { SIGN_IN_EMAIL_STORAGE_KEY } from '../LoginByEmail/LoginByEmail';
import styles from './SignWithLink.module.scss';

type Problem = 'invalidEmail' | 'unknown' | null;

/**
 * Wejście z linku w e-mailu. Gdy link otwarto w tej samej przeglądarce, adres jest zapamiętany
 * i logowanie rusza samo („Logowanie…”); w innej — prosimy o adres. Wygasły link wraca na ekran
 * logowania linkiem z komunikatem (jak dotąd, przez `?error=`).
 */
export const SignWithLink: FunctionComponent = () => {
  const { t } = useTranslation(['auth']);
  const dispatch = useAppDispatch();
  const query = useQuery();
  const [email, setEmail] = useState(
    () =>
      window.localStorage.getItem(SIGN_IN_EMAIL_STORAGE_KEY) ??
      query.get('email')?.replace(' ', '+') ??
      ''
  );
  const [busy, setBusy] = useState(Boolean(email));
  const [problem, setProblem] = useState<Problem>(null);
  const started = useRef(false);

  const signIn = useCallback(
    (address: string) => {
      setBusy(true);
      setProblem(null);
      dispatch(
        userActions.loginByEmailLink(
          { href: window.location.href, email: address },
          () => {
            const redirectTo = query.get('redirectTo');
            // Bez zdejmowania „Logowanie…”: zaraz przekierowanie (redirectTo albo AuthChecker po zalogowaniu),
            // a powrót do formularza mignąłby na chwilę.
            if (redirectTo) window.location.replace(decodeURIComponent(redirectTo));
          },
          (code) => {
            const kind = authErrorKind(code);
            if (kind === 'expiredLink' && code) {
              const target = new URL('/auth/login-by-link', window.location.origin);
              target.searchParams.set('error', code);
              // Adres wraca do formularza nowego linku — nie trzeba go wpisywać drugi raz.
              target.searchParams.set('email', address);
              window.location.replace(target);

              return;
            }
            if (kind === 'invalidEmail') {
              window.localStorage.removeItem(SIGN_IN_EMAIL_STORAGE_KEY);
              setEmail('');
            }
            setProblem(kind === 'invalidEmail' ? 'invalidEmail' : 'unknown');
            setBusy(false);
          }
        )
      );
    },
    [dispatch, query]
  );

  // Adres znany → logujemy od razu, raz (StrictMode montuje efekt dwukrotnie).
  useEffect(() => {
    if (started.current || !email) return;
    started.current = true;
    signIn(email);
  }, [email, signIn]);

  const autoSigningIn = busy && Boolean(email) && !problem;

  return (
    <AuthCard
      footer={
        autoSigningIn ? undefined : <Link to="/auth/login">{t('auth:screens.sent.back')}</Link>
      }
    >
      {autoSigningIn ? (
        <>
          <AuthHeading
            title={t('auth:screens.signWithLink.title')}
            lead={t('auth:screens.signWithLink.lead')}
          />
          <div className={styles.loading} role="status">
            <Spin size="small" />
            {t('auth:screens.signWithLink.wait')}
          </div>
        </>
      ) : (
        <>
          <AuthHeading
            title={t('auth:screens.signWithLink.confirmTitle')}
            lead={t('auth:screens.signWithLink.confirmLead')}
          />
          <EmailForm
            name="signWithLink"
            initialEmail={email}
            submitLabel={t('auth:screens.signWithLink.submit')}
            busyLabel={t('auth:screens.signWithLink.busy')}
            busy={busy}
            onSubmit={(address) => {
              // Przed setEmail: inaczej efekt auto-startu zobaczy adres i wyśle drugie żądanie z tym
              // samym, jednorazowym kodem (drugie kończy się invalid-action-code).
              started.current = true;
              setEmail(address);
              signIn(address);
            }}
            notice={
              problem ? (
                <Callout
                  tone="error"
                  title={problem === 'unknown' ? t('auth:screens.errors.unknown.title') : undefined}
                  focusOnMount
                >
                  {problem === 'invalidEmail'
                    ? t('auth:screens.errors.invalidEmail')
                    : t('auth:screens.errors.unknown.text')}
                </Callout>
              ) : undefined
            }
          />
        </>
      )}
    </AuthCard>
  );
};
